import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type PointerEvent as ReactPointerEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
  duplicateRef,
  emptyPlan,
  fitsSheet,
  hitTest,
  LINE_KINDS,
  moveRef,
  nearestWall,
  newId,
  OPENING_CATEGORY,
  OPENING_KINDS,
  openingShapes,
  parseLength,
  PLAN_DISCLAIMER,
  PLAN_SCALES,
  PLAN_SHEETS,
  planBounds,
  planLegend,
  removeRef,
  roomArea,
  setWallInner,
  setWallLength,
  symbolK,
  symbolAngle,
  symbolXY,
  syncRooms,
  createRoom,
  OPENING_DEFAULTS,
  magnetSymbol,
  wallFrame,
  openingEdges,
  setOpeningLeft,
  wallInner,
  type RoomBasis,
  formatMeters,
  wallDims,
  wallLength,
  rotateRef,
  snapPoint,
  SYMBOL_CATEGORIES,
  SYMBOL_MM,
  symbolDef,
  symbolSize,
  SYMBOLS,
  textSize,
  WALL_PRESETS,
  type LineKind,
  type OpeningKind,
  type PlanData,
  type PlanDetail,
  type PlanRef,
  type PlanSheet,
  type PlanSummary,
  type Prim,
} from "@handvero/shared";
import { api, errorText } from "../api";
import { EmptyState, Icon, Message, PdfViewer, Sheet, useUnauthorizedRedirect } from "./ui";
import { RoomDialog, StartDialog, WallView } from "./PlanWallView";
import "./plans.css";

const emptyPlanForIcon = emptyPlan();

/*
 * Zeichenprogramm (Zusatzabo 1): Installationspläne mit Drag and Drop. Alle Längen sind Zentimeter der Wirklichkeit;
 * Symbole und Schrift haben auf dem Papier eine feste Größe. Der Plan speichert sich von selbst, das PDF entsteht
 * auf dem Server (gleicher Aufbau wie in der Vorschau hier).
 */

const PAPER = "#ffffff";
const INK = "#1b1d22";
const WALL = "#8f929a";
const HILITE = "#2f6fd0";

const day = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

/* ---------- Liste ---------- */

export function PlansView(props: { projectId: string; planId?: string; base: string }) {
  if (props.planId) return <PlanEditor key={props.planId} planId={props.planId} backTo={props.base} />;
  return <PlanList projectId={props.projectId} base={props.base} />;
}

function PlanList({ projectId, base }: { projectId: string; base: string }) {
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const [list, setList] = useState<PlanSummary[] | null>(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("Erdgeschoss");
  const sheet: PlanSheet = "A3";
  const scale = 50;

  const load = useCallback(() => {
    api
      .get<PlanSummary[]>(`/api/projects/${projectId}/plans`)
      .then(setList)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [projectId, onAuth]);
  useEffect(load, [load]);

  async function create() {
    try {
      const p = await api.post<PlanDetail>(`/api/projects/${projectId}/plans`, { title: title.trim() || "Plan", sheet, scale });
      navigate(`${base}/${p.id}`);
    } catch (err) {
      setError(errorText(err));
      setCreating(false);
    }
  }

  async function remove(p: PlanSummary) {
    if (!window.confirm(`Plan „${p.title}“ endgültig löschen? Bereits abgelegte PDFs im Projektordner bleiben erhalten.`)) return;
    try {
      await api.del(`/api/plans/${p.id}`);
      setList((l) => (l ?? []).filter((x) => x.id !== p.id));
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div className="plan-list">
      <Message>{error}</Message>
      {!list && !error && <p className="muted-s">Wird geladen …</p>}
      {list?.length === 0 && (
        <EmptyState
          title="Noch keine Pläne"
          text="Zeichnen Sie Installationspläne für Haus und Wohnung: Wände ziehen, Steckdosen, Schalter und Leuchten per Drag and Drop setzen, Leitungen einzeichnen. Die Legende zählt automatisch mit, das PDF liegt danach im Projektordner."
          words={["Zeichnen", "Planen"]}
        />
      )}
      {list && list.length > 0 && (
        <ul className="plan-cards">
          {list.map((p) => (
            <li key={p.id} className="plan-card">
              <Link to={`${base}/${p.id}`} className="plan-card-link">
                <span className="plan-sheet">
                  {p.sheet}
                  <small>1:{p.scale}</small>
                </span>
                <span className="plan-card-text">
                  <b>{p.title}</b>
                  <small>
                    {p.symbolCount} {p.symbolCount === 1 ? "Symbol" : "Symbole"} · geändert {day(p.updatedAt)}
                    {p.createdBy && ` · ${p.createdBy}`}
                  </small>
                </span>
              </Link>
              <button className="dbtn ghost small" onClick={() => void remove(p)} aria-label={`${p.title} löschen`}>
                <Icon name="trash" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <button className="dbtn" onClick={() => setCreating(true)}>
          <Icon name="plus" /> Neuer Plan
        </button>
      </div>
      <p className="plan-legal">{PLAN_DISCLAIMER}</p>
      {creating && (
        <Sheet title="Neuer Plan" onClose={() => setCreating(false)}>
          <form
            className="plan-form"
            onSubmit={(e) => {
              e.preventDefault();
              void create();
            }}
          >
            <label>
              Name des Plans
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} autoFocus />
            </label>
            <p className="muted-s">Blattgröße, Maßstab und den ersten Raum fragen wir beim Öffnen des Plans ab.</p>
            <button className="dbtn" type="submit">
              Plan anlegen
            </button>
          </form>
        </Sheet>
      )}
    </div>
  );
}

/* ---------- Zeichnen ---------- */

function Glyph({ prims, sw = 1.3 }: { prims: Prim[]; sw?: number }) {
  return (
    <>
      {prims.map((p, i) => {
        if (p.t === "l") return <line key={i} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke={INK} strokeWidth={sw} strokeLinecap="round" />;
        if (p.t === "c") return <circle key={i} cx={p.x} cy={p.y} r={p.r} fill={p.f ? INK : PAPER} stroke={INK} strokeWidth={sw} />;
        if (p.t === "r") return <rect key={i} x={p.x} y={p.y} width={p.w} height={p.h} fill={p.f ? INK : PAPER} stroke={INK} strokeWidth={sw} />;
        if (p.t === "p") {
          const d = p.pts.map((q, j) => `${j ? "L" : "M"}${q[0]} ${q[1]}`).join(" ") + (p.closed || p.f ? " Z" : "");
          return <path key={i} d={d} fill={p.f ? INK : "none"} stroke={INK} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round" />;
        }
        return (
          <text key={i} x={p.x} y={p.y} fontSize={p.size} fontWeight={700} textAnchor="middle" dominantBaseline="central" fill={INK} fontFamily="Roboto, Segoe UI, Arial, sans-serif">
            {p.s}
          </text>
        );
      })}
    </>
  );
}

function SymbolIcon({ id, size = 30 }: { id: string; size?: number }) {
  const def = symbolDef(id);
  if (!def) return null;
  return (
    <svg width={size} height={size} viewBox="-11 -11 22 22" aria-hidden="true">
      <rect x="-11" y="-11" width="22" height="22" fill={PAPER} />
      <Glyph prims={def.prims} />
    </svg>
  );
}

/** Zahlenfeld, das beim Verlassen oder mit Enter übernommen wird. Eingaben wie 4,85 oder 485 werden verstanden. */
function NumField(props: { label: string; value: number; onCommit: (v: number) => void; min?: number; max?: number; unit?: string; hint?: string; setMsg: (m: string) => void }) {
  const { min = 0, max = 100000 } = props;
  const text = (Math.round(props.value * 10) / 10).toString().replace(".", ",");
  return (
    <label>
      {props.label}
      <input
        key={text}
        defaultValue={text}
        inputMode="decimal"
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          const v = /^\d+([.,]\d+)?$/.test(raw) ? Number(raw.replace(",", ".")) : null;
          if (v === null || v < min || v > max) return props.setMsg(`Bitte eine Zahl zwischen ${min} und ${max}${props.unit ? ` ${props.unit}` : ""} eingeben.`);
          props.setMsg("");
          if (Math.abs(v - props.value) > 0.0001) props.onCommit(v);
        }}
      />
    </label>
  );
}

function OpeningIcon({ kind, size = 30 }: { kind: OpeningKind; size?: number }) {
  const d: PlanData = {
    ...emptyPlanForIcon,
    walls: [{ id: "w", x1: -60, y1: 0, x2: 60, y2: 0, t: 20 }],
    openings: [{ id: "o", wallId: "w", pos: 60, w: kind === "doppeltuer" ? 80 : 56, kind, flip: false, mirror: false }],
  };
  const shape = openingShapes(d, d.openings[0]!);
  if (!shape) return null;
  return (
    <svg width={size} height={size} viewBox="-70 -40 140 80" aria-hidden="true">
      <rect x="-70" y="-40" width="140" height="80" fill={PAPER} />
      <line x1="-60" y1="0" x2="60" y2="0" stroke={WALL} strokeWidth="20" />
      <polygon points={shape.gap.map((p) => p.join(",")).join(" ")} fill={PAPER} />
      {shape.strokes.map((st, i) => (
        <polyline key={i} points={st.map((p) => p.join(",")).join(" ")} fill="none" stroke={INK} strokeWidth="2.4" />
      ))}
    </svg>
  );
}

type Tool = "select" | "wall" | "room" | "line" | "text" | "symbol" | "opening";
type Draft = { kind: "wall"; last: [number, number] } | { kind: "line"; pts: [number, number][] } | { kind: "room"; a: [number, number] } | null;

const HINTS: Record<Tool, string> = {
  select: "Klicken zum Auswählen, ziehen zum Verschieben. Leere Fläche ziehen verschiebt die Ansicht, mit dem Mausrad zoomen.",
  wall: "Klicken setzt die Wandecken nacheinander. Oder die lichte Länge eintippen und Enter: 485 sind 4,85 m, genauso 4,85. Doppelklick oder Esc beendet.",
  room: "Mit gedrückter Maustaste ein Rechteck aufziehen. Name und Fläche stehen danach im Plan.",
  line: "Klicken setzt die Knickpunkte der Leitung. Doppelklick oder Enter beendet sie, Esc bricht ab.",
  text: "Klicken, wo der Text stehen soll. Den Inhalt tragen Sie rechts ein.",
  opening: "Auf eine Wand klicken, dort entsteht das Fenster oder die Tür. Danach lässt sich die Stelle ziehen, mit R wechselt der Aufschlag.",
  symbol: "Klicken setzt das Symbol, mehrfach möglich. Esc beendet. Mit R drehen Sie das markierte Symbol.",
};

/** Endpunkt einer Wand mit eingetippter Länge, in Richtung des Mauszeigers (sonst nach rechts). */
function typedEnd(from: [number, number], cursor: [number, number] | null, text: string, wallT: number): [number, number] | null {
  const inner = parseLength(text);
  if (inner === null) return null;
  // Eingetippt wird das lichte Maß; bis Wandmitte kommt an jedem Ende die halbe Wandstärke dazu.
  const len = inner + wallT;
  let dx = cursor ? cursor[0] - from[0] : 1;
  let dy = cursor ? cursor[1] - from[1] : 0;
  const n = Math.hypot(dx, dy);
  if (n < 1) {
    dx = 1;
    dy = 0;
  } else {
    dx /= n;
    dy /= n;
  }
  return [Math.round((from[0] + dx * len) * 10) / 10, Math.round((from[1] + dy * len) * 10) / 10];
}

function PlanEditor({ planId, backTo }: { planId: string; backTo: string }) {
  const onAuth = useUnauthorizedRedirect();
  const [title, setTitle] = useState("");
  const [data, setData] = useState<PlanData | null>(null);
  const [error, setError] = useState("");
  const [save, setSave] = useState<"saved" | "saving" | "error">("saved");
  const [notice, setNotice] = useState("");
  const [pdf, setPdf] = useState<string | null>(null);

  const [past, setPast] = useState<PlanData[]>([]);
  const [future, setFuture] = useState<PlanData[]>([]);
  const dragBase = useRef<PlanData | null>(null);

  const [tool, setTool] = useState<Tool>("select");
  const [symbolId, setSymbolId] = useState("steckdose");
  const [wallT, setWallT] = useState<number>(WALL_PRESETS[2].t);
  const [lineKind, setLineKind] = useState<LineKind>("leitung");
  const [sel, setSel] = useState<PlanRef | null>(null);
  const [draft, setDraft] = useState<Draft>(null);
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const [grid, setGrid] = useState(10);
  const [snapOn, setSnapOn] = useState(true);
  const [ortho, setOrtho] = useState(true);
  const [view, setView] = useState({ x: -100, y: -100, zoom: 0.8 });
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [category, setCategory] = useState(SYMBOL_CATEGORIES[0]!);
  const [openKind, setOpenKind] = useState<OpeningKind>("fenster");
  const [wallView, setWallView] = useState<string | null>(null);
  const [roomDialog, setRoomDialog] = useState(false);
  const [startDialog, setStartDialog] = useState(false);
  const [showRooms, setShowRooms] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [lenBuf, setLenBuf] = useState("");
  const [lenMsg, setLenMsg] = useState("");

  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const interaction = useRef<null | { kind: "drag"; ref: PlanRef; start: [number, number] } | { kind: "pan"; sx: number; sy: number; vx: number; vy: number } | { kind: "room"; a: [number, number] }>(null);
  const dirty = useRef(false);
  const fitted = useRef(false);

  /* Laden */
  useEffect(() => {
    api
      .get<PlanDetail>(`/api/plans/${planId}`)
      .then((p) => {
        setTitle(p.title);
        setData(p.data);
        // Neuer, leerer Plan: zuerst Blatt und Raum abfragen.
        const d = p.data;
        if (!d.walls.length && !d.rooms.length && !d.symbols.length && !d.lines.length && !d.texts.length) setStartDialog(true);
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [planId, onAuth]);

  /* Größe der Zeichenfläche */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [data !== null]);

  const fit = useCallback(
    (d: PlanData) => {
      const b = planBounds(d);
      if (!b) return setView({ x: -100, y: -100, zoom: Math.min(size.w, size.h) / 1200 });
      const pad = 80;
      const zoom = Math.max(0.02, Math.min(4, Math.min(size.w / (b.maxX - b.minX + 2 * pad), size.h / (b.maxY - b.minY + 2 * pad))));
      setView({ zoom, x: b.minX - pad - (size.w / zoom - (b.maxX - b.minX + 2 * pad)) / 2, y: b.minY - pad - (size.h / zoom - (b.maxY - b.minY + 2 * pad)) / 2 });
    },
    [size.w, size.h],
  );
  useEffect(() => {
    if (data && !fitted.current && size.w > 100) {
      fitted.current = true;
      fit(data);
    }
  }, [data, size.w, fit]);

  /* Verlauf */
  const dataRef = useRef<PlanData | null>(null);
  dataRef.current = data;
  const pastRef = useRef<PlanData[]>([]);
  const futureRef = useRef<PlanData[]>([]);
  const setHistory = (p: PlanData[], f: PlanData[]) => {
    pastRef.current = p;
    futureRef.current = f;
    setPast(p);
    setFuture(f);
  };
  const commit = useCallback((raw: PlanData) => {
    const next = syncRooms(raw);
    const cur = dataRef.current;
    if (cur) setHistory([...pastRef.current.slice(-99), cur], []);
    dataRef.current = next;
    setData(next);
    dirty.current = true;
  }, []);
  const undo = useCallback(() => {
    const prev = pastRef.current[pastRef.current.length - 1];
    const cur = dataRef.current;
    if (!prev || !cur) return;
    setHistory(pastRef.current.slice(0, -1), [cur, ...futureRef.current]);
    dataRef.current = prev;
    setData(prev);
    setSel(null);
    dirty.current = true;
  }, []);
  const redo = useCallback(() => {
    const next = futureRef.current[0];
    const cur = dataRef.current;
    if (!next || !cur) return;
    setHistory([...pastRef.current, cur], futureRef.current.slice(1));
    dataRef.current = next;
    setData(next);
    dirty.current = true;
  }, []);

  /* Automatisch speichern */
  const saveNow = useCallback(async () => {
    if (!data || !dirty.current) return true;
    dirty.current = false;
    setSave("saving");
    try {
      await api.put(`/api/plans/${planId}`, { title: title.trim() || "Plan", data });
      setSave("saved");
      return true;
    } catch (err) {
      dirty.current = true;
      setSave("error");
      setError(errorText(err));
      return false;
    }
  }, [data, title, planId]);
  const saveRef = useRef(saveNow);
  saveRef.current = saveNow;
  useEffect(() => {
    if (!dirty.current) return;
    setSave("saving");
    const t = window.setTimeout(() => void saveRef.current(), 1100);
    return () => window.clearTimeout(t);
  }, [data, title]);
  useEffect(() => {
    const leave = () => void saveRef.current();
    return leave;
  }, []);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  /* Hilfen */
  const worldOf = useCallback(
    (clientX: number, clientY: number): [number, number] => {
      const r = svgRef.current!.getBoundingClientRect();
      return [(clientX - r.left) / view.zoom + view.x, (clientY - r.top) / view.zoom + view.y];
    },
    [view],
  );
  const pointer = useCallback(
    (e: { clientX: number; clientY: number; shiftKey: boolean }, from?: [number, number]): [number, number] => {
      let [x, y] = worldOf(e.clientX, e.clientY);
      if (!data) return [x, y];
      if (snapOn) [x, y] = snapPoint(data, x, y, grid, 12 / view.zoom);
      if (from && ortho !== e.shiftKey) {
        if (Math.abs(x - from[0]) >= Math.abs(y - from[1])) y = from[1];
        else x = from[0];
      }
      return [Math.round(x * 10) / 10, Math.round(y * 10) / 10];
    },
    [worldOf, data, snapOn, grid, view.zoom, ortho],
  );

  const finishLine = useCallback(
    (d: Draft) => {
      if (d?.kind === "line" && d.pts.length >= 2 && data) {
        const id = newId();
        commit({ ...data, lines: [...data.lines, { id, kind: lineKind, pts: d.pts }] });
        setSel({ kind: "line", id });
      }
      setDraft(null);
    },
    [data, commit, lineKind],
  );

  const choose = (t: Tool) => {
    if (draft?.kind === "line") finishLine(draft);
    setDraft(null);
    setTool(t);
    if (t !== "select") setSel(null);
  };

  /* Zeiger */
  function onPointerDown(e: ReactPointerEvent<SVGSVGElement>) {
    if (!data) return;
    if (e.button === 1 || e.button === 2) {
      interaction.current = { kind: "pan", sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    const from = draft?.kind === "wall" ? draft.last : draft?.kind === "line" ? draft.pts[draft.pts.length - 1] : undefined;
    const p = pointer(e, from);
    if (tool === "select") {
      const hit = hitTest(data, ...worldOf(e.clientX, e.clientY), 10 / view.zoom);
      setSel(hit);
      if (hit) {
        dragBase.current = data;
        interaction.current = { kind: "drag", ref: hit, start: p };
      } else {
        interaction.current = { kind: "pan", sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
      }
      e.currentTarget.setPointerCapture(e.pointerId);
    } else if (tool === "symbol") {
      const id = newId();
      const m = magnetSymbol(data, p[0], p[1], 1, undefined, 14 / view.zoom);
      commit({ ...data, symbols: [...data.symbols, { id, type: symbolId, x: m[0], y: m[1], rot: 0 }] });
      setSel({ kind: "symbol", id });
    } else if (tool === "opening") {
      const [wx, wy] = worldOf(e.clientX, e.clientY);
      const hit = nearestWall(data, wx, wy, 14 / view.zoom);
      const width = OPENING_KINDS[openKind].w;
      if (!hit || wallLength(hit.wall) < width) {
        setLenMsg(hit ? "Diese Wand ist kürzer als das Bauteil. Wählen Sie eine längere Wand oder ändern Sie die Breite danach." : "Bitte direkt auf eine Wand klicken.");
        return;
      }
      setLenMsg("");
      const id = newId();
      const pos = Math.min(wallLength(hit.wall) - width / 2, Math.max(width / 2, Math.round(hit.pos / 5) * 5));
      commit({ ...data, openings: [...data.openings, { id, wallId: hit.wall.id, pos, w: width, kind: openKind, flip: false, mirror: false }] });
      setSel({ kind: "opening", id });
    } else if (tool === "wall") {
      setLenBuf("");
      if (draft?.kind === "wall") {
        if (p[0] === draft.last[0] && p[1] === draft.last[1]) return;
        commit({ ...data, walls: [...data.walls, { id: newId(), x1: draft.last[0], y1: draft.last[1], x2: p[0], y2: p[1], t: wallT }] });
      }
      setDraft({ kind: "wall", last: p });
    } else if (tool === "line") {
      const pts = draft?.kind === "line" ? draft.pts : [];
      const last = pts[pts.length - 1];
      if (last && last[0] === p[0] && last[1] === p[1]) return;
      setDraft({ kind: "line", pts: [...pts, p] });
    } else if (tool === "room") {
      interaction.current = { kind: "room", a: p };
      setDraft({ kind: "room", a: p });
      e.currentTarget.setPointerCapture(e.pointerId);
    } else if (tool === "text") {
      const id = newId();
      commit({ ...data, texts: [...data.texts, { id, x: p[0], y: p[1], s: "Text", rot: 0 }] });
      setSel({ kind: "text", id });
      setTool("select");
    }
  }

  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    if (!data) return;
    const it = interaction.current;
    if (it?.kind === "pan") {
      setView((v) => ({ ...v, x: it.vx - (e.clientX - it.sx) / v.zoom, y: it.vy - (e.clientY - it.sy) / v.zoom }));
      return;
    }
    const from = draft?.kind === "wall" ? draft.last : draft?.kind === "line" ? draft.pts[draft.pts.length - 1] : undefined;
    const p = pointer(e, from);
    setCursor(tool === "symbol" ? magnetSymbol(data, p[0], p[1], 1, undefined, 14 / view.zoom) : p);
    if (it?.kind === "drag" && dragBase.current) {
      let [dx, dy] = [p[0] - it.start[0], p[1] - it.start[1]];
      // Freie Symbole rasten mit ihrem Quadrat an anderen Symbolen ein.
      const moved = it.ref.kind === "symbol" ? dragBase.current.symbols.find((s) => s.id === it.ref.id) : undefined;
      if (moved && !moved.wallId) {
        const m = magnetSymbol(dragBase.current, moved.x + dx, moved.y + dy, symbolK(moved), moved.id, 14 / view.zoom);
        dx = m[0] - moved.x;
        dy = m[1] - moved.y;
      }
      setData(syncRooms(moveRef(dragBase.current, it.ref, dx, dy)));
      dirty.current = true;
    }
  }

  function onPointerUp(e: ReactPointerEvent<SVGSVGElement>) {
    const it = interaction.current;
    interaction.current = null;
    if (!data || !it) return;
    if (it.kind === "drag" && dragBase.current) {
      const base = dragBase.current;
      dragBase.current = null;
      if (base !== data) setHistory([...pastRef.current.slice(-99), base], []);
    } else if (it.kind === "room") {
      const p = pointer(e);
      const x = Math.min(it.a[0], p[0]);
      const y = Math.min(it.a[1], p[1]);
      const w = Math.abs(p[0] - it.a[0]);
      const h = Math.abs(p[1] - it.a[1]);
      setDraft(null);
      if (w >= 30 && h >= 30) {
        const id = newId();
        commit({ ...data, rooms: [...data.rooms, { id, name: "Raum", x, y, w, h }] });
        setSel({ kind: "room", id });
        setTool("select");
      }
    }
  }

  function onDoubleClick() {
    if (draft?.kind === "line") finishLine(draft);
    else if (draft?.kind === "wall") setDraft(null);
  }

  function onDrop(e: DragEvent<SVGSVGElement>) {
    const raw = e.dataTransfer.getData("text/plain");
    if (!data || !raw.startsWith("hv-symbol:")) return;
    e.preventDefault();
    const type = raw.slice("hv-symbol:".length);
    if (!symbolDef(type)) return;
    const p0 = pointer(e);
    const p = magnetSymbol(data, p0[0], p0[1], 1, undefined, 14 / view.zoom);
    const id = newId();
    commit({ ...data, symbols: [...data.symbols, { id, type, x: p[0], y: p[1], rot: 0 }] });
    setSel({ kind: "symbol", id });
    setTool("select");
  }

  /* Mausrad: zoomen um den Mauszeiger */
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = svg.getBoundingClientRect();
      setView((v) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const zoom = Math.max(0.02, Math.min(6, v.zoom * factor));
        const wx = (e.clientX - r.left) / v.zoom + v.x;
        const wy = (e.clientY - r.top) / v.zoom + v.y;
        return { zoom, x: wx - (e.clientX - r.left) / zoom, y: wy - (e.clientY - r.top) / zoom };
      });
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [data !== null]);

  /* Tasten */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;
      if (!data) return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (tool === "wall" && draft?.kind === "wall" && !ctrl) {
        // Länge tippen: Ziffern, Komma, Punkt; Enter setzt die Wand in Richtung des Mauszeigers.
        if (/^[0-9.,]$/.test(e.key)) {
          e.preventDefault();
          setLenMsg("");
          setLenBuf((b) => (b + e.key).slice(0, 10));
          return;
        }
        if (e.key === "Backspace" && lenBuf) {
          e.preventDefault();
          setLenBuf((b) => b.slice(0, -1));
          return;
        }
        if (e.key === "Escape" && lenBuf) {
          setLenBuf("");
          return;
        }
        if (e.key === "Enter" && lenBuf) {
          e.preventDefault();
          const end = typedEnd(draft.last, cursor, lenBuf, wallT);
          if (!end) return setLenMsg("Diese Länge verstehe ich nicht. Beispiele: 485 (cm) oder 4,85 (m).");
          commit({ ...data, walls: [...data.walls, { id: newId(), x1: draft.last[0], y1: draft.last[1], x2: end[0], y2: end[1], t: wallT }] });
          setDraft({ kind: "wall", last: end });
          setLenBuf("");
          return;
        }
      }
      if (ctrl && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (ctrl && e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      } else if (ctrl && e.key.toLowerCase() === "d" && sel) {
        e.preventDefault();
        const r = duplicateRef(data, sel, grid * 2);
        commit(r.data);
        setSel(r.ref);
      } else if (e.key === "Escape") {
        if (draft?.kind === "line") finishLine(draft);
        setDraft(null);
        if (!draft) {
          setTool("select");
          setSel(null);
        }
      } else if (e.key === "Enter" && draft?.kind === "line") {
        finishLine(draft);
      } else if ((e.key === "Delete" || e.key === "Backspace") && sel) {
        commit(removeRef(data, sel));
        setSel(null);
      } else if (e.key.toLowerCase() === "r" && sel && !ctrl) {
        commit(rotateRef(data, sel));
      } else if (sel && e.key.startsWith("Arrow")) {
        e.preventDefault();
        const step = e.shiftKey ? grid * 5 : grid;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        commit(moveRef(data, sel, dx, dy));
      } else if (!ctrl) {
        const map: Record<string, Tool> = { v: "select", w: "wall", b: "room", l: "line", t: "text" };
        const t = map[e.key.toLowerCase()];
        if (t) choose(t);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* Abgeleitetes */
  const legend = useMemo(() => (data ? planLegend(data) : null), [data]);
  const sheetFit = useMemo(() => (data ? fitsSheet(data) : null), [data]);

  async function showPdf() {
    setNotice("");
    if (await saveNow()) setPdf(`/api/plans/${planId}/pdf`);
  }
  async function filePdf() {
    setNotice("");
    if (!(await saveNow())) return;
    try {
      await api.post(`/api/plans/${planId}/file`, {});
      setNotice("Das PDF liegt jetzt im Projektordner „Pläne“.");
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (!data) {
    return (
      <div className="plan-ed">
        <div className="plan-ed-top">
          <Link to={backTo} className="dbtn ghost small">
            ← Pläne
          </Link>
        </div>
        <div className="plan-ed-loading">{error ? <Message>{error}</Message> : "Plan wird geladen …"}</div>
      </div>
    );
  }

  const scale = data.scale;
  const sym = symbolSize(scale);
  const th = textSize(scale);
  const lw = (mm: number) => (mm * scale) / 10; // Linienstärke in Zentimetern der Wirklichkeit
  const selected = sel;
  const selSymbol = sel?.kind === "symbol" ? data.symbols.find((s) => s.id === sel.id) : undefined;
  const selWall = sel?.kind === "wall" ? data.walls.find((s) => s.id === sel.id) : undefined;
  const selRoom = sel?.kind === "room" ? data.rooms.find((s) => s.id === sel.id) : undefined;
  const selLine = sel?.kind === "line" ? data.lines.find((s) => s.id === sel.id) : undefined;
  const selOpening = sel?.kind === "opening" ? data.openings.find((s) => s.id === sel.id) : undefined;
  const openingEdge = selOpening ? openingEdges(data, selOpening) : null;
  const selText = sel?.kind === "text" ? data.texts.find((s) => s.id === sel.id) : undefined;

  const patch = (next: Partial<PlanData>) => commit({ ...data, ...next });
  const patchSel = <K extends "symbols" | "walls" | "rooms" | "lines" | "texts" | "openings">(key: K, fields: Partial<PlanData[K][number]>) => {
    if (!sel) return;
    commit({ ...data, [key]: (data[key] as { id: string }[]).map((x) => (x.id === sel.id ? { ...x, ...fields } : x)) } as PlanData);
  };

  const gridVisible = showGrid && view.zoom * grid >= 5;

  return createPortal(
    <div className="plan-ed" role="application" aria-label="Zeichenprogramm">
      <div className="plan-ed-top">
        <Link to={backTo} className="dbtn ghost small" onClick={() => void saveNow()}>
          ← Pläne
        </Link>
        <input
          className="plan-title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            dirty.current = true;
          }}
          maxLength={150}
          aria-label="Name des Plans"
        />
        <span className={`plan-save ${save}`}>{save === "saved" ? "Gespeichert" : save === "saving" ? "Speichert …" : "Nicht gespeichert"}</span>
        <button className="dbtn ghost small" onClick={undo} disabled={!past.length} title="Rückgängig (Strg+Z)">
          Zurück
        </button>
        <button className="dbtn ghost small" onClick={redo} disabled={!future.length} title="Wiederholen (Strg+Y)">
          Wiederholen
        </button>
        <button className="dbtn ghost small" onClick={() => void showPdf()}>
          PDF ansehen
        </button>
        <button className="dbtn small" onClick={() => void filePdf()}>
          PDF im Projekt ablegen
        </button>
      </div>
      {(error || notice) && (
        <div className="plan-ed-msg">
          <Message>{error}</Message>
          <Message kind="ok">{notice}</Message>
        </div>
      )}

      <aside className="plan-left" aria-label="Werkzeuge und Symbole">
        <div className="plan-tools">
          {(
            [
              ["select", "Auswählen", "V"],
              ["wall", "Wand", "W"],
              ["room", "Raum", "B"],
              ["line", "Leitung", "L"],
              ["text", "Text", "T"],
            ] as [Tool, string, string][]
          ).map(([t, label, key]) => (
            <button key={t} className={tool === t ? "on" : undefined} onClick={() => choose(t)} aria-pressed={tool === t}>
              {label}
              <kbd>{key}</kbd>
            </button>
          ))}
        </div>
        <button className="dbtn small plan-room-btn" onClick={() => setRoomDialog(true)}>
          Raum mit Maßen anlegen
        </button>
        {tool === "wall" && (
          <label className="plan-opt">
            Wandstärke
            <select value={wallT} onChange={(e) => setWallT(Number(e.target.value))}>
              {WALL_PRESETS.map((w) => (
                <option key={w.t} value={w.t}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {tool === "line" && (
          <label className="plan-opt">
            Art der Leitung
            <select value={lineKind} onChange={(e) => setLineKind(e.target.value as LineKind)}>
              {(Object.keys(LINE_KINDS) as LineKind[]).map((k) => (
                <option key={k} value={k}>
                  {LINE_KINDS[k].name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="plan-cats" role="tablist" aria-label="Symbolgruppen">
          {[...SYMBOL_CATEGORIES, OPENING_CATEGORY].map((c) => (
            <button key={c} role="tab" aria-selected={category === c} className={category === c ? "on" : undefined} onClick={() => setCategory(c)}>
              {c}
            </button>
          ))}
        </div>
        {category === OPENING_CATEGORY && (
          <ul className="plan-symbols">
            {(Object.keys(OPENING_KINDS) as OpeningKind[]).map((k) => (
              <li key={k}>
                <button
                  className={tool === "opening" && openKind === k ? "on" : undefined}
                  onClick={() => {
                    setOpenKind(k);
                    setDraft(null);
                    setSel(null);
                    setTool("opening");
                  }}
                  title="Anklicken, dann auf die Wand klicken"
                >
                  <OpeningIcon kind={k} />
                  <span>
                    {OPENING_KINDS[k].name}
                    <br />
                    <small>{OPENING_KINDS[k].w} cm</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <ul className="plan-symbols">
          {category !== OPENING_CATEGORY && SYMBOLS.filter((s) => s.category === category).map((s) => (
            <li key={s.id}>
              <button
                className={tool === "symbol" && symbolId === s.id ? "on" : undefined}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", `hv-symbol:${s.id}`);
                  e.dataTransfer.effectAllowed = "copy";
                }}
                onClick={() => {
                  setSymbolId(s.id);
                  setDraft(null);
                  setSel(null);
                  setTool("symbol");
                }}
                title="Anklicken und im Plan setzen, oder direkt in den Plan ziehen"
              >
                <SymbolIcon id={s.id} />
                <span>{s.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="plan-canvas" ref={wrapRef}>
        <svg
          ref={svgRef}
          width={size.w}
          height={size.h}
          className={`plan-svg tool-${tool}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onDoubleClick={onDoubleClick}
          onContextMenu={(e) => e.preventDefault()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
        >
          <defs>
            <pattern id="plan-grid" width={grid} height={grid} patternUnits="userSpaceOnUse">
              <circle cx="0" cy="0" r={Math.max(0.6, 1.1 / view.zoom)} fill="#aab3c2" />
            </pattern>
            <pattern id="plan-grid-m" width={100} height={100} patternUnits="userSpaceOnUse">
              <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#d4dae4" strokeWidth={1 / view.zoom} />
            </pattern>
          </defs>
          <rect width={size.w} height={size.h} fill={PAPER} />
          <g transform={`scale(${view.zoom}) translate(${-view.x} ${-view.y})`}>
            {showGrid && <rect x={view.x} y={view.y} width={size.w / view.zoom} height={size.h / view.zoom} fill="url(#plan-grid-m)" />}
            {gridVisible && <rect x={view.x} y={view.y} width={size.w / view.zoom} height={size.h / view.zoom} fill="url(#plan-grid)" />}

            {showRooms && data.rooms.map((r) => (
              <g key={r.id}>
                <rect x={r.x} y={r.y} width={r.w} height={r.h} fill="#eef2f8" stroke="#c4ccd9" strokeWidth={lw(0.25)} />
                <text x={r.x + r.w / 2} y={r.y + r.h / 2} fontSize={th} fill="#6b7482" textAnchor="middle" dominantBaseline="central" fontFamily="Roboto, Segoe UI, Arial, sans-serif" pointerEvents="none">
                  {r.name || "Raum"} {roomArea(r).toLocaleString("de-DE")} m²
                </text>
              </g>
            ))}
            {data.walls.map((w) => (
              <line key={w.id} x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} stroke={WALL} strokeWidth={w.t} strokeLinecap="square" />
            ))}
            {data.openings.map((o) => {
              const shape = openingShapes(data, o);
              if (!shape) return null;
              return (
                <g key={o.id}>
                  <polygon points={shape.gap.map((p) => p.join(",")).join(" ")} fill={PAPER} />
                  {shape.strokes.map((st, i) => (
                    <polyline key={i} points={st.map((p) => p.join(",")).join(" ")} fill="none" stroke={INK} strokeWidth={lw(0.3)} strokeLinejoin="round" />
                  ))}
                </g>
              );
            })}
            {data.dims &&
              wallDims(data).map((dm) => (
                <text key={dm.id} x={dm.x} y={dm.y} fontSize={Math.max(th * 0.85, 11 / view.zoom)} fill="#5a606a" textAnchor="middle" dominantBaseline="central" transform={`rotate(${dm.angle} ${dm.x} ${dm.y})`} fontFamily="Roboto, Segoe UI, Arial, sans-serif" pointerEvents="none">
                  {dm.text}
                </text>
              ))}
            {data.lines.map((l) => {
              const dash = LINE_KINDS[l.kind].dash;
              let best = 1;
              let bestLen = 0;
              for (let i = 1; i < l.pts.length; i++) {
                const len = Math.hypot(l.pts[i]![0] - l.pts[i - 1]![0], l.pts[i]![1] - l.pts[i - 1]![1]);
                if (len > bestLen) {
                  bestLen = len;
                  best = i;
                }
              }
              const a = l.pts[best - 1]!;
              const b = l.pts[best]!;
              return (
                <g key={l.id}>
                  <polyline
                    points={l.pts.map((p) => p.join(",")).join(" ")}
                    fill="none"
                    stroke={INK}
                    strokeWidth={lw(0.35)}
                    strokeDasharray={dash ? dash.map((d) => d * lw(0.3)).join(" ") : undefined}
                    strokeLinejoin="round"
                  />
                  {l.label && (
                    <text x={(a[0] + b[0]) / 2 + th * 0.3} y={(a[1] + b[1]) / 2 - th * 0.3} fontSize={th * 0.8} fill={INK} fontFamily="Roboto, Segoe UI, Arial, sans-serif">
                      {l.label}
                    </text>
                  )}
                </g>
              );
            })}
            {data.symbols.map((s) => {
              const def = symbolDef(s.type);
              if (!def) return null;
              const at = symbolXY(data, s);
              const size = sym * symbolK(s);
              // h = Montagehöhe über dem fertigen Fußboden.
              const caption = [s.label, s.h !== undefined ? `h${s.h}` : null].filter(Boolean).join(" · ");
              return (
                <g key={s.id}>
                  <g transform={`translate(${at.x} ${at.y}) rotate(${symbolAngle(data, s)}) scale(${size / 20})`}>
                    <Glyph prims={def.prims} sw={1.3} />
                  </g>
                  {caption && (
                    <text x={at.x + size / 2 + th * 0.2} y={at.y + th * 0.4} fontSize={th * 0.8} fill={INK} fontFamily="Roboto, Segoe UI, Arial, sans-serif">
                      {caption}
                    </text>
                  )}
                </g>
              );
            })}
            {data.texts.map((t) => (
              <text key={t.id} x={t.x} y={t.y} fontSize={th} fill={INK} transform={`rotate(${-t.rot} ${t.x} ${t.y})`} fontFamily="Roboto, Segoe UI, Arial, sans-serif">
                {t.s}
              </text>
            ))}

            {/* Auswahl */}
            {selected &&
              selSymbol &&
              (() => {
                const at = symbolXY(data, selSymbol);
                const half = sym * symbolK(selSymbol) * 0.65;
                return <rect x={at.x - half} y={at.y - half} width={half * 2} height={half * 2} fill="none" stroke={HILITE} strokeWidth={2 / view.zoom} strokeDasharray={`${6 / view.zoom} ${4 / view.zoom}`} />;
              })()}
            {selOpening && (() => {
              const shape = openingShapes(data, selOpening);
              return shape ? <polygon points={shape.gap.map((p) => p.join(",")).join(" ")} fill="none" stroke={HILITE} strokeWidth={2.5 / view.zoom} /> : null;
            })()}
            {selWall && <line x1={selWall.x1} y1={selWall.y1} x2={selWall.x2} y2={selWall.y2} stroke={HILITE} strokeOpacity={0.45} strokeWidth={selWall.t + 6 / view.zoom} strokeLinecap="square" />}
            {selLine && <polyline points={selLine.pts.map((p) => p.join(",")).join(" ")} fill="none" stroke={HILITE} strokeOpacity={0.5} strokeWidth={6 / view.zoom} />}
            {selRoom && <rect x={selRoom.x} y={selRoom.y} width={selRoom.w} height={selRoom.h} fill="none" stroke={HILITE} strokeWidth={2 / view.zoom} />}
            {selText && <rect x={selText.x - th * 0.2} y={selText.y - th} width={Math.max(1, selText.s.length) * th * 0.6 + th * 0.4} height={th * 1.4} fill="none" stroke={HILITE} strokeWidth={2 / view.zoom} />}

            {/* Vorschau beim Zeichnen */}
            {draft?.kind === "wall" &&
              cursor &&
              (() => {
                const end = lenBuf ? (typedEnd(draft.last, cursor, lenBuf, wallT) ?? cursor) : cursor;
                const len = Math.hypot(end[0] - draft.last[0], end[1] - draft.last[1]);
                return (
                  <g pointerEvents="none">
                    <line x1={draft.last[0]} y1={draft.last[1]} x2={end[0]} y2={end[1]} stroke={HILITE} strokeOpacity={0.55} strokeWidth={wallT} strokeLinecap="square" />
                    {len > 5 && (
                      <text x={(draft.last[0] + end[0]) / 2} y={(draft.last[1] + end[1]) / 2 - wallT / 2 - th * 0.6} fontSize={th} fontWeight={700} fill={HILITE} textAnchor="middle" fontFamily="Roboto, Segoe UI, Arial, sans-serif">
                        {lenBuf ? `${lenBuf} → ` : ""}
                        {formatMeters(Math.max(0, len - wallT))}
                      </text>
                    )}
                  </g>
                );
              })()}
            {draft?.kind === "line" && (
              <polyline points={[...draft.pts, ...(cursor ? [cursor] : [])].map((p) => p.join(",")).join(" ")} fill="none" stroke={HILITE} strokeWidth={lw(0.4)} strokeLinejoin="round" />
            )}
            {draft?.kind === "room" && cursor && (
              <rect x={Math.min(draft.a[0], cursor[0])} y={Math.min(draft.a[1], cursor[1])} width={Math.abs(cursor[0] - draft.a[0])} height={Math.abs(cursor[1] - draft.a[1])} fill={HILITE} fillOpacity={0.08} stroke={HILITE} strokeWidth={2 / view.zoom} />
            )}
            {tool === "symbol" && cursor && symbolDef(symbolId) && (
              <g opacity={0.55} transform={`translate(${cursor[0]} ${cursor[1]}) scale(${sym / 20})`} pointerEvents="none">
                <Glyph prims={symbolDef(symbolId)!.prims} sw={1.3} />
              </g>
            )}
            {cursor && tool !== "select" && tool !== "symbol" && <circle cx={cursor[0]} cy={cursor[1]} r={4 / view.zoom} fill={HILITE} pointerEvents="none" />}
          </g>
        </svg>
        <div className="plan-zoom">
          <button onClick={() => setView((v) => ({ ...v, zoom: Math.min(6, v.zoom * 1.25) }))} aria-label="Vergrößern">
            +
          </button>
          <button onClick={() => setView((v) => ({ ...v, zoom: Math.max(0.02, v.zoom / 1.25) }))} aria-label="Verkleinern">
            −
          </button>
          <button onClick={() => fit(data)}>Alles zeigen</button>
        </div>
        <p className="plan-hint">{lenMsg || HINTS[tool]}</p>
      </div>

      <aside className="plan-right" aria-label="Eigenschaften">
        <section className="plan-card2">
          <h3>Auswahl</h3>
          {!sel && <p className="muted-s">Nichts ausgewählt. Klicken Sie ein Teil im Plan an, um es zu bearbeiten.</p>}
          {selSymbol && (
            <>
              <p className="plan-sel-name">{symbolDef(selSymbol.type)?.name}</p>
              <label>
                Bezeichnung (z. B. Q1, S2)
                <input value={selSymbol.label ?? ""} maxLength={80} onChange={(e) => patchSel("symbols", { label: e.target.value })} />
              </label>
              <NumField label="Montagehöhe über Fußboden (cm)" value={selSymbol.h ?? 0} max={1000} setMsg={setLenMsg} onCommit={(v) => patchSel("symbols", { h: v })} />
              <NumField label="Größe (%)" value={Math.round(symbolK(selSymbol) * 100)} min={30} max={400} setMsg={setLenMsg} onCommit={(v) => patchSel("symbols", { k: v / 100 })} />
              {selSymbol.wallId && selSymbol.pos !== undefined ? (
                (() => {
                  const f = wallFrame(data, selSymbol.wallId);
                  return (
                    <>
                      <NumField
                        label="Abstand von der linken Wandfläche (cm)"
                        value={selSymbol.pos - (f?.a0 ?? 0)}
                        max={100000}
                        setMsg={setLenMsg}
                        onCommit={(v) => patchSel("symbols", { pos: v + (f?.a0 ?? 0) })}
                      />
                      <button className="dbtn ghost small" onClick={() => setWallView(selSymbol.wallId!)}>
                        Wandansicht öffnen
                      </button>
                    </>
                  );
                })()
              ) : (
                <>
                  <NumField label="Position von links (cm)" value={selSymbol.x} max={2000000} setMsg={setLenMsg} onCommit={(v) => patchSel("symbols", { x: v })} />
                  <NumField label="Position von oben (cm)" value={selSymbol.y} max={2000000} setMsg={setLenMsg} onCommit={(v) => patchSel("symbols", { y: v })} />
                </>
              )}
            </>
          )}
          {selWall && (
            <label>
              Lichte Länge (z. B. 485 oder 4,85)
              <input
                key={`${selWall.id}-${Math.round(wallInner(data, selWall) * 10)}`}
                defaultValue={(Math.round(wallInner(data, selWall)) / 100).toFixed(2).replace(".", ",")}
                inputMode="decimal"
                onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                onBlur={(e) => {
                  const cm = parseLength(e.target.value);
                  if (cm === null) return setLenMsg("Diese Länge verstehe ich nicht. Beispiele: 485 (cm) oder 4,85 (m).");
                  setLenMsg("");
                  if (Math.abs(cm - wallInner(data, selWall)) >= 0.5) commit(setWallInner(data, selWall.id, cm));
                }}
              />
            </label>
          )}
          {selWall && (
            <button className="dbtn small" onClick={() => setWallView(selWall.id)}>
              Wandansicht: Steckdosen, Schalter …
            </button>
          )}
          {selWall && (
            <label>
              Wandstärke
              <select value={selWall.t} onChange={(e) => patchSel("walls", { t: Number(e.target.value) })}>
                {[...new Set([...WALL_PRESETS.map((w) => w.t), selWall.t])].map((t) => (
                  <option key={t} value={t}>
                    {t.toLocaleString("de-DE")} cm
                  </option>
                ))}
              </select>
            </label>
          )}
          {selOpening && (
            <>
              <p className="plan-sel-name">{OPENING_KINDS[selOpening.kind].name}</p>
              <label>
                Breite (cm)
                <input
                  key={`${selOpening.id}-w-${selOpening.w}`}
                  defaultValue={selOpening.w}
                  inputMode="decimal"
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  onBlur={(e) => {
                    const cm = parseLength(e.target.value);
                    if (cm === null || cm < 20 || cm > 1000) return setLenMsg("Bitte eine Breite zwischen 20 und 1000 cm eingeben.");
                    setLenMsg("");
                    patchSel("openings", { w: cm });
                  }}
                />
              </label>
              {openingEdge && (
                <>
                  <NumField
                    label="Abstand zur linken Ecke (cm)"
                    value={openingEdge.left}
                    max={Math.max(0, openingEdge.inner)}
                    setMsg={setLenMsg}
                    onCommit={(v) => commit(setOpeningLeft(data, selOpening.id, v))}
                  />
                  <NumField
                    label="Abstand zur rechten Ecke (cm)"
                    value={openingEdge.right}
                    max={Math.max(0, openingEdge.inner)}
                    setMsg={setLenMsg}
                    onCommit={(v) => commit(setOpeningLeft(data, selOpening.id, openingEdge.inner - selOpening.w - v))}
                  />
                  <p className="muted-s">Gemessen von den Wandflächen, links und rechts wie beim Blick von innen auf die Wand.</p>
                </>
              )}
              <NumField label="Höhe der Öffnung (cm)" value={selOpening.h ?? OPENING_DEFAULTS[selOpening.kind].h} min={20} max={600} setMsg={setLenMsg} onCommit={(v) => patchSel("openings", { h: v })} />
              {selOpening.kind === "fenster" && (
                <NumField label="Brüstungshöhe (cm)" value={selOpening.sill ?? OPENING_DEFAULTS.fenster.sill} max={600} setMsg={setLenMsg} onCommit={(v) => patchSel("openings", { sill: v })} />
              )}
              {selOpening.kind !== "fenster" && selOpening.kind !== "durchgang" && (
                <>
                  <label className="plan-check">
                    <input type="checkbox" checked={selOpening.flip} onChange={(e) => patchSel("openings", { flip: e.target.checked })} /> Tür öffnet zur anderen Wandseite
                  </label>
                  {selOpening.kind !== "doppeltuer" && (
                    <label className="plan-check">
                      <input type="checkbox" checked={selOpening.mirror} onChange={(e) => patchSel("openings", { mirror: e.target.checked })} /> Angeln auf der anderen Seite
                    </label>
                  )}
                </>
              )}
            </>
          )}
          {selRoom && (
            <>
              <label>
                Name des Raums
                <input value={selRoom.name} maxLength={80} onChange={(e) => patchSel("rooms", { name: e.target.value })} />
              </label>
              <NumField
                label="Breite lichtes Maß (cm)"
                value={selRoom.w}
                min={30}
                setMsg={setLenMsg}
                onCommit={(v) => {
                  if (selRoom.wallIds?.length === 4) commit(setWallInner(data, selRoom.wallIds[0]!, v));
                  else patchSel("rooms", { w: v });
                }}
              />
              <NumField
                label="Länge lichtes Maß (cm)"
                value={selRoom.h}
                min={30}
                setMsg={setLenMsg}
                onCommit={(v) => {
                  if (selRoom.wallIds?.length === 4) commit(setWallInner(data, selRoom.wallIds[1]!, v));
                  else patchSel("rooms", { h: v });
                }}
              />
              <NumField label="Deckenhöhe (cm)" value={selRoom.height ?? data.ceiling} min={150} max={1000} setMsg={setLenMsg} onCommit={(v) => patchSel("rooms", { height: v })} />
              <p className="muted-s">
                {roomArea(selRoom).toLocaleString("de-DE")} m² · {(selRoom.w / 100).toLocaleString("de-DE")} × {(selRoom.h / 100).toLocaleString("de-DE")} m
              </p>
            </>
          )}
          {selLine && (
            <>
              <label>
                Art
                <select value={selLine.kind} onChange={(e) => patchSel("lines", { kind: e.target.value as LineKind })}>
                  {(Object.keys(LINE_KINDS) as LineKind[]).map((k) => (
                    <option key={k} value={k}>
                      {LINE_KINDS[k].name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Beschriftung (z. B. NYM-J 3x1,5)
                <input value={selLine.label ?? ""} maxLength={80} onChange={(e) => patchSel("lines", { label: e.target.value })} />
              </label>
            </>
          )}
          {selText && (
            <label>
              Text
              <input value={selText.s} maxLength={200} autoFocus onChange={(e) => patchSel("texts", { s: e.target.value })} />
            </label>
          )}
          {sel && (
            <div className="plan-sel-actions">
              {(sel.kind === "symbol" || sel.kind === "text") && (
                <button className="dbtn ghost small" onClick={() => commit(rotateRef(data, sel))}>
                  Drehen (R)
                </button>
              )}
              <button
                className="dbtn ghost small"
                onClick={() => {
                  const r = duplicateRef(data, sel, grid * 2);
                  commit(r.data);
                  setSel(r.ref);
                }}
              >
                Kopieren
              </button>
              <button
                className="dbtn ghost small danger"
                onClick={() => {
                  commit(removeRef(data, sel));
                  setSel(null);
                }}
              >
                Löschen
              </button>
            </div>
          )}
        </section>

        <details className="plan-card2 plan-fold">
          <summary>Blatt und Maßstab</summary>
          <label>
            Größe
            <select value={data.sheet} onChange={(e) => patch({ sheet: e.target.value as PlanSheet })}>
              {(Object.keys(PLAN_SHEETS) as PlanSheet[]).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ausrichtung
            <select value={data.portrait ? "hoch" : "quer"} onChange={(e) => patch({ portrait: e.target.value === "hoch" })}>
              <option value="quer">Querformat</option>
              <option value="hoch">Hochformat</option>
            </select>
          </label>
          <label>
            Maßstab
            <select value={data.scale} onChange={(e) => patch({ scale: Number(e.target.value) })}>
              {[...new Set([...PLAN_SCALES, data.scale])]
                .sort((a, b) => a - b)
                .map((s) => (
                  <option key={s} value={s}>
                    1:{s}
                  </option>
                ))}
            </select>
          </label>
          <NumField label="Standard-Deckenhöhe für neue Räume (cm)" value={data.ceiling} min={150} max={1000} setMsg={setLenMsg} onCommit={(v) => patch({ ceiling: v })} />
          <label>
            Höhen der Installationszonen über dem Fußboden (cm); die Zone unter der Decke kommt automatisch dazu
            <input
              key={data.zones.join(",")}
              defaultValue={data.zones.join(", ")}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              onBlur={(e) => {
                const nums = e.target.value
                  .split(/[;,\s]+/)
                  .filter(Boolean)
                  .map((x) => Number(x.replace(",", ".")));
                if (nums.some((n) => !Number.isFinite(n) || n < 0 || n > 1000) || nums.length > 8) return setLenMsg("Bitte Höhen in cm mit Komma getrennt eingeben, z. B. 30, 105.");
                setLenMsg("");
                patch({ zones: nums });
              }}
            />
          </label>
          {sheetFit && sheetFit.widthMm > 0 && (
            <p className={sheetFit.fits ? "plan-fit ok" : "plan-fit bad"}>
              {sheetFit.fits
                ? `Passt auf das Blatt (${Math.round(sheetFit.widthMm)} × ${Math.round(sheetFit.heightMm)} mm).`
                : `Zu groß fürs Blatt (${Math.round(sheetFit.widthMm)} × ${Math.round(sheetFit.heightMm)} mm). Wählen Sie ein größeres Blatt oder einen kleineren Maßstab.`}
            </p>
          )}
        </details>

        <details className="plan-card2 plan-fold" open>
          <summary>Ansicht und Zeichenhilfen</summary>
          <label className="plan-check">
            <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} /> Raster anzeigen
          </label>
          <label className="plan-check">
            <input type="checkbox" checked={showRooms} onChange={(e) => setShowRooms(e.target.checked)} /> Raumflächen und Raumnamen anzeigen
          </label>
          <label>
            Raster
            <select value={grid} onChange={(e) => setGrid(Number(e.target.value))}>
              {[5, 10, 25, 50].map((g) => (
                <option key={g} value={g}>
                  {g} cm
                </option>
              ))}
            </select>
          </label>
          <label className="plan-check">
            <input type="checkbox" checked={data.dims} onChange={(e) => patch({ dims: e.target.checked })} /> Wandmaße im Plan anzeigen
          </label>
          <label className="plan-check">
            <input type="checkbox" checked={snapOn} onChange={(e) => setSnapOn(e.target.checked)} /> Am Raster und an Ecken einrasten
          </label>
          <label className="plan-check">
            <input type="checkbox" checked={ortho} onChange={(e) => setOrtho(e.target.checked)} /> Rechtwinklig zeichnen (Umschalt = frei)
          </label>
        </details>

        <details className="plan-card2 plan-fold">
          <summary>Legende</summary>
          {legend && legend.symbols.length === 0 && legend.lines.length === 0 && <p className="muted-s">Sobald Sie Symbole setzen, zählt die Legende automatisch mit.</p>}
          <ul className="plan-legend">
            {legend?.symbols.map((e) => (
              <li key={e.symbolId}>
                <SymbolIcon id={e.symbolId} size={22} />
                <span>{e.name}</span>
                <b>{e.count}</b>
              </li>
            ))}
            {legend?.lines.map((e) => (
              <li key={e.kind}>
                <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
                  <line x1="1" y1="11" x2="21" y2="11" stroke={INK} strokeWidth="1.6" strokeDasharray={LINE_KINDS[e.kind].dash?.map((d) => d * 1.2).join(" ")} />
                </svg>
                <span>{e.name}</span>
                <b>{e.meters.toLocaleString("de-DE")} m</b>
              </li>
            ))}
          </ul>
        </details>
        <p className="plan-legal">
          Symbolgröße auf dem Papier: {SYMBOL_MM} mm. {PLAN_DISCLAIMER}
        </p>
      </aside>
      {pdf && <PdfViewer url={pdf} title={title || "Plan"} onClose={() => setPdf(null)} />}
      {wallView && <WallView data={data} wallId={wallView} onCommit={commit} onClose={() => setWallView(null)} onSelectWall={setWallView} />}
      {startDialog && (
        <StartDialog
          defaultHeight={data.ceiling}
          onClose={() => setStartDialog(false)}
          onDone={(r) => {
            let next: PlanData = { ...data, sheet: r.sheet, portrait: r.portrait, scale: r.scale };
            if (r.room) {
              const c = createRoom(next, r.room);
              next = c.data;
              setSel({ kind: "room", id: c.roomId });
            }
            commit(next);
            setStartDialog(false);
            fit(syncRooms(next));
          }}
        />
      )}
      {roomDialog && (
        <RoomDialog
          defaultHeight={data.ceiling}
          onClose={() => setRoomDialog(false)}
          onCreate={(o) => {
            const r = createRoom(data, o);
            commit(r.data);
            setSel({ kind: "room", id: r.roomId });
            setTool("select");
            setRoomDialog(false);
            fit(syncRooms(r.data));
          }}
        />
      )}
    </div>,
    document.querySelector(".hv-app") ?? document.body,
  );
}
